/**
 * useOverlapWarningDialog — Track 3 Phase 2
 *
 * Owns the P7 "Insertion overlap" confirmation dialog feature extracted
 * from `App.tsx` as part of the Track 3 App.tsx decomposition (see
 * `docs/plans/2026-05-08_track3_app_decomposition.md`).
 *
 * Responsibilities:
 *   - Owns the `pendingOverlapInsert` local state (the command + insertion
 *     point + overlap region currently waiting for the user's go/no-go).
 *   - Exposes `interceptOverlapForBlockInsert(command)` — the dispatch
 *     interceptor used by `useDraftSession` and by the INSERT confirm path
 *     in `useInsertBlockDialog`. Returns `true` when an overlap is found
 *     and the command has been stashed (consumer must defer dispatch);
 *     `false` otherwise.
 *   - Owns the Continue / Cancel handlers (Continue replays the command
 *     via `dispatch`; Cancel mirrors ESC semantics).
 *
 * Returned shape:
 *   {
 *     isOpen,
 *     props: { insertionPoint, overlapRegion, onContinue, onCancel },
 *     interceptOverlapForBlockInsert,
 *   }
 *
 * The orchestrator spreads `props` onto `<OverlapWarningDialog>` and
 * threads `interceptOverlapForBlockInsert` into both `useDraftSession`
 * and `useInsertBlockDialog` so both surface the warning consistently.
 *
 * AutoCAD parity:
 *   AutoCAD does not have a literal "overlap warning" dialog; this is the
 *   project-specific P7 footprint check. The hook keeps the original
 *   semantics intact — Continue dispatches, Cancel logs `feed.esc` and
 *   clears the draft session.
 */

import { useCallback, useState } from "react";

import type { EditorCommand } from "@dxf-ai-editor/contracts";

import type { DimensionStyle } from "@dxf-ai-editor/drawing-model";

import { computeBlockBounds } from "../components/insertBlockHelpers";
import {
  detectInsertionOverlap,
  resolveDimensionStyleBounds,
  type OverlapInfo,
} from "../scene/insertionOverlap";
import { getEntityMetadataIndex, useEditorStore } from "../state/editorStore";
import { SHELL_ENTITY_PREVIEW_THRESHOLD } from "../state/dxfGeometry";
import { useT } from "../i18n";

import type { DraftSession } from "../scene/draftingHelpers";

interface PendingOverlapInsert {
  command: EditorCommand;
  insertionPoint: { x: number; y: number };
  overlap: OverlapInfo;
}

export interface UseOverlapWarningDialogParams {
  /** Cleared by Continue / Cancel handlers (matches App-scope cleanup). */
  setDraftSession: (session: DraftSession | null) => void;
  /** Cleared by Continue / Cancel handlers. */
  setCommandText: (text: string) => void;
  /** Receives the localized "block inserted" / "feed.esc" entries. */
  appendCommandFeed: (text: string) => void;
}

export interface UseOverlapWarningDialogResult {
  /** Whether to mount `<OverlapWarningDialog>`. */
  isOpen: boolean;
  /** Props bundle for `<OverlapWarningDialog>`. */
  props: {
    insertionPoint: { x: number; y: number };
    overlapRegion: { left: number; right: number; top: number; bottom: number };
    onContinue: () => void;
    onCancel: () => void;
  } | null;
  /**
   * Interceptor for `geometry/create-block-insert` commands. Pass to
   * `useDraftSession.interceptGeometryDispatch` and call from the INSERT
   * dialog confirm path. Returns `true` when an overlap was detected and
   * the command has been stashed; in that case the caller must defer
   * dispatch — the Continue handler will replay it.
   */
  interceptOverlapForBlockInsert: (command: EditorCommand) => boolean;
}

export function useOverlapWarningDialog({
  setDraftSession,
  setCommandText,
  appendCommandFeed,
}: UseOverlapWarningDialogParams): UseOverlapWarningDialogResult {
  const t = useT();
  const dispatch = useEditorStore((store) => store.dispatch);
  const geometryDocument = useEditorStore((store) => store.geometryDocument);

  const [pending, setPending] = useState<PendingOverlapInsert | null>(null);

  const interceptOverlapForBlockInsert = useCallback(
    (command: EditorCommand): boolean => {
      if (command.type !== "geometry/create-block-insert") return false;
      const blockDef = geometryDocument.blocks?.[command.blockName];
      if (!blockDef) return false;

      // codex PR #472 finding 1 (P1) — `geometryDocument.spaces[i].entities`
      // is a read-through GETTER that snapshots the ENTIRE compact cache for
      // the space into full `DrawingEntity[]` payloads (geometry arrays
      // included — see `spaceReadThrough.ts`'s module doc), not the
      // capped/sentinel-emptied shell-preview list `state.drawing.entities`
      // uses above `SHELL_ENTITY_PREVIEW_THRESHOLD` specifically so huge
      // imports never pay an O(N) full-materialization cost on the render
      // path (`editorSelectors.ts:103-115`). Reading `.entities`
      // unconditionally here on EVERY block insert would materialize a
      // 100k+-entity space synchronously before the command even dispatches
      // — INSERT could hang or exhaust memory on the exact documents that
      // cap exists to protect. `getEntityMetadataIndex().size` is a plain
      // `Map.size` read (no per-entity work, no map copies) — gate on it
      // BEFORE ever touching `.entities`, and degrade gracefully (skip the
      // overlap check; the INSERT proceeds unwarned) above the SAME
      // threshold the rest of the app already treats as "huge import —
      // capped/preview-only views".
      if (getEntityMetadataIndex().size > SHELL_ENTITY_PREVIEW_THRESHOLD) {
        return false;
      }

      // codex PR #472 round-4 finding 4 + round-5 finding 1 — the raw typed
      // entities lose the DIMSTYLE-table enrichment the legacy
      // `state.drawing.entities` path applied via `enrichDimensionEntities` /
      // `buildDimensionPreview`. Build the DIMSTYLE lookup ONCE and thread it
      // into EVERY bounds site so each DIMENSION's referenced DIMSTYLE (DIMEXE /
      // DIMGAP / DIMEXO / DIMTXT) is materialized onto its typed `styleOverrides`
      // before cad-core's `dimensionBounds` runs — for TOP-LEVEL existing
      // dimensions (`resolveDimensionStyleBounds` below), for dimensions nested
      // inside an EXISTING Block INSERT (via `detectInsertionOverlap`'s
      // `dimStylesByName` → specialization), AND for dimensions nested inside the
      // INSERTED block itself (via `computeBlockBounds`). Otherwise an INSERT
      // overlapping only that style-driven padding zone slips through unwarned.
      const dimStylesByName = new Map<string, DimensionStyle>();
      for (const s of geometryDocument.styleTables?.dimensionStyles ?? []) {
        dimStylesByName.set(s.name, s);
      }

      const bounds = computeBlockBounds(
        blockDef,
        geometryDocument.blocks,
        dimStylesByName,
      );
      // D1(a2) S12 — read the TYPED entities of the target space directly
      // off `geometryDocument.spaces` (already available here) instead of
      // the legacy `EntityPreview` cache, so `detectInsertionOverlap` can
      // resolve each existing entity's bounds via cad-core's typed
      // `getEntityBounds` (see that module's doc for the coverage gain).
      const rawExistingEntities =
        geometryDocument.spaces.find((space) => space.id === command.spaceId)
          ?.entities ?? [];
      const existingEntities = resolveDimensionStyleBounds(
        rawExistingEntities,
        dimStylesByName,
      );
      const overlap = detectInsertionOverlap(
        command.insertionPoint,
        {
          minX: bounds.minX,
          minY: bounds.minY,
          maxX: bounds.maxX,
          maxY: bounds.maxY,
        },
        existingEntities,
        command.scaleX,
        command.scaleY,
        { blockDefinitions: geometryDocument.blocks },
        dimStylesByName,
      );
      if (!overlap) return false;
      setPending({
        command,
        insertionPoint: command.insertionPoint,
        overlap,
      });
      return true;
    },
    [geometryDocument],
  );

  const onContinue = useCallback(() => {
    if (!pending) return;
    dispatch(pending.command);
    if (pending.command.type === "geometry/create-block-insert") {
      appendCommandFeed(
        t("summary.block_inserted", { name: pending.command.blockName }),
      );
    }
    setPending(null);
    setDraftSession(null);
    setCommandText("");
  }, [
    pending,
    dispatch,
    appendCommandFeed,
    t,
    setDraftSession,
    setCommandText,
  ]);

  const onCancel = useCallback(() => {
    // ESC semantics — abort the insertion without dispatching.
    setPending(null);
    appendCommandFeed(t("feed.esc"));
    setDraftSession(null);
    setCommandText("");
  }, [appendCommandFeed, t, setDraftSession, setCommandText]);

  return {
    isOpen: pending !== null,
    props: pending
      ? {
          insertionPoint: pending.insertionPoint,
          overlapRegion: pending.overlap.combined,
          onContinue,
          onCancel,
        }
      : null,
    interceptOverlapForBlockInsert,
  };
}
